import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { Button } from "@/components/ui/button";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useBasket();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    if (result === "cancel") { setStatus("cancelled"); return; }
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { clear(); setStatus("paid"); return; }
        if (data.status === "expired" || polls.current > 12) { setStatus("error"); return; }
      } catch { if (polls.current > 12) { setStatus("error"); return; } }
      polls.current += 1;
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [result, params, clear]);

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center min-h-[70vh]">
      {status === "checking" && (<><Loader2 className="animate-spin text-[#1A3C2A] mx-auto" size={40} /><p className="mt-4 text-[#5A626A]">Confirming your payment…</p></>)}
      {status === "paid" && (
        <div className="animate-fade-up">
          <CheckCircle2 className="text-[#1A3C2A] mx-auto" size={60} />
          <h1 data-testid="payment-success-title" className="font-display text-2xl font-extrabold text-[#1A3C2A] mt-4">Order confirmed!</h1>
          <p className="text-sm text-[#5A626A] mt-2">Thank you for supporting local sellers. Your sellers have been notified and will prepare your order.</p>
          <Button data-testid="view-orders-button" onClick={() => navigate("/orders")} className="mt-6 rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">View my orders</Button>
          <Link to="/browse" className="block mt-3 text-sm font-semibold text-[#E07A5F]">Continue shopping</Link>
        </div>
      )}
      {(status === "cancelled" || status === "error") && (
        <div className="animate-fade-up">
          <XCircle className="text-[#E07A5F] mx-auto" size={60} />
          <h1 className="font-display text-2xl font-extrabold text-[#1A3C2A] mt-4">{status === "cancelled" ? "Payment cancelled" : "Something went wrong"}</h1>
          <p className="text-sm text-[#5A626A] mt-2">Your basket is still saved. You can try checking out again.</p>
          <Button onClick={() => navigate("/basket")} className="mt-6 rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">Back to basket</Button>
        </div>
      )}
    </div>
  );
}
